import React, { useState, useContext, useEffect } from "react";
import ApiService from "../../ApiService";
import { Container, Row, Col, Card, Form, Button } from "react-bootstrap";
import { IoSend } from "react-icons/io5";
import PageTitle from "../Common/PageTitle/PageTitle";
import statesList from "../../util/statesList";
import { SnackbarContext } from "../AlertSystem";
import { MonitorService } from "../../util/captureException";

const isProd = process.env.REACT_APP_ENV === "prod";

const PRODUCTS = isProd
  ? [
      { name: "3-Panel STD Test", variantId: "UHJvZHVjdFZhcmlhbnQ6Nw==" },
      { name: "5-Panel STD Test", variantId: "UHJvZHVjdFZhcmlhbnQ6MQ==" },
      { name: "9-Panel STD Test", variantId: "UHJvZHVjdFZhcmlhbnQ6NDU=" },
      { name: "3 Site: Chlamydia & Gonorrhea", variantId: "UHJvZHVjdFZhcmlhbnQ6MTg=" },
      { name: "Bacterial Vaginosis", variantId: "UHJvZHVjdFZhcmlhbnQ6Mjg=" },
      { name: "HPV (vagina havers)", variantId: "UHJvZHVjdFZhcmlhbnQ6Nzk=" },
      { name: "Mycoplasma Genitalium", variantId: "UHJvZHVjdFZhcmlhbnQ6NDA=" }
    ]
  : [
      { name: "3-Panel STD Test", variantId: "UHJvZHVjdFZhcmlhbnQ6MzUx" },
      { name: "5-Panel STD Test", variantId: "UHJvZHVjdFZhcmlhbnQ6MzMz" },
      { name: "9-Panel STD Test", variantId: "UHJvZHVjdFZhcmlhbnQ6NDA1" },
      { name: "3 Site: Chlamydia & Gonorrhea", variantId: "UHJvZHVjdFZhcmlhbnQ6MzY5" },
      { name: "Bacterial Vaginosis", variantId: "UHJvZHVjdFZhcmlhbnQ6Mzg1" },
      { name: "HPV (vagina havers)", variantId: "UHJvZHVjdFZhcmlhbnQ6NDM4" },
      { name: "Mycoplasma Genitalium", variantId: "UHJvZHVjdFZhcmlhbnQ6Mzkw" }
    ];

const SHIPPING = isProd
  ? [
      {
        name: "USPS Priority (1-3 Days) - $10",
        variantId: "U2hpcHBpbmdNZXRob2Q6Nw=="
      },
      {
        name: "USPS Priority (1-3 Days) - $0",
        variantId: "U2hpcHBpbmdNZXRob2Q6Njc="
      }
    ]
  : [
      {
        name: "USPS Priority (1-3 Days) - $10",
        variantId: "U2hpcHBpbmdNZXRob2Q6NTUx"
      },
      {
        name: "UPS or USPS Next Day (1-2 Days) - $0",
        variantId: "U2hpcHBpbmdNZXRob2Q6NTU1"
      }
    ];

const CreateOrder = ({ resetView }) => {
  const { showSnackbar } = useContext(SnackbarContext);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [variantId, setVariantId] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [streetAddress2, setStreetAddress2] = useState("");
  const [locality, setLocality] = useState("");
  const [region, setRegion] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [shippingMethodId, setShippingMethodId] = useState(SHIPPING[1].variantId || "");
  const [invoiceAmount, setInvoiceAmount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const patientId = new URLSearchParams(window.location.search).get("patientId");
  const kitName = new URLSearchParams(window.location.search).get("kitName");
  const [patientDetail, setPatientDetail] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const fetchPatientDetails = async () => {
    if (!patientId) {
      showSnackbar({ message: "Patient ID is required to fetch details", severity: "error" });
      return;
    }
    setIsLoading(true);
    setIsError(false);
    try {
      const res = await ApiService.getPatientDetailsBySubId(patientId);
      if (res?.data) {
        setPatientDetail(res.data);
        setFirstName(res.data.firstName || "");
        setLastName(res.data.lastName || "");
        setEmail(res.data.email || "");
        setPhone(res.data.phoneNumber || "");
        setStreetAddress(res.data.address?.street_address || "");
        setStreetAddress2(res.data.address?.street_address2 || "");
        setLocality(res.data.address?.locality || "");
        setRegion(res.data.address?.region || "");
        setPostalCode(res.data.address?.postal_code || "");
        // Try to match kitName to a product if present, after setting patient details
        if (kitName && !variantId) {
          const normalizedKitName = kitName.toLowerCase().replace(/[^a-z0-9]/gi, "");
          const matchedProduct = PRODUCTS.find(product => {
            const normalizedProductName = product.name.toLowerCase().replace(/[^a-z0-9]/gi, "");
            return normalizedKitName.includes(normalizedProductName.slice(0, 6)) || normalizedProductName.includes(normalizedKitName.slice(0, 6));
          });
          if (matchedProduct) {
            setVariantId(matchedProduct.variantId);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching patient details:", error);
      setIsError(true);
      showSnackbar({ message: "Error fetching patient details", severity: "error" });
      MonitorService.captureException(error)
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (patientId && !isLoading && !isError && !patientDetail?.firstName) {
      fetchPatientDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inputStyle = {
    background: "#f9f9f9",
    border: "1px solid #ccc",
    boxShadow: "none",
    padding: "14px 16px",
    borderRadius: "8px",
    color: "#333",
    width: "100%",
    marginTop: "4px",
    height: "48px"
  };

  const labelStyle = {
    color: "#333",
    fontWeight: "600",
    marginBottom: "8px",
    fontSize: "0.9rem",
    textTransform: "uppercase"
  };

  const handleSubmit = async e => {
    e.preventDefault();

    // Trim inputs
    const fName = firstName.trim();
    const lName = lastName.trim();
    const userEmail = email.trim();
    const userPhone = phone.trim();
    const street = streetAddress.trim();
    const city = locality.trim();
    const state = region.trim();
    const zip = postalCode.trim();

    // Validation
    if (!fName) {
      showSnackbar({ message: "First name is required", severity: "warning" });
      return;
    }
    if (!lName) {
      showSnackbar({ message: "Last name is required", severity: "warning" });
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!userEmail || !emailRegex.test(userEmail)) {
      showSnackbar({ message: "Valid email is required", severity: "warning" });
      return;
    }
    const normalizedPhone = userPhone.replace(/(?!^\+)\D/g, "");
    if (!/^\+\d{11}$/.test(normalizedPhone)) {
      showSnackbar({ message: "Phone must be in format +14047897890", severity: "warning" });
      return;
    }
    setPhone(normalizedPhone);

    if (!street) {
      showSnackbar({ message: "Street address is required", severity: "warning" });
      return;
    }
    if (!city) {
      showSnackbar({ message: "City is required", severity: "warning" });
      return;
    }
    if (!state) {
      showSnackbar({ message: "Region is required", severity: "warning" });
      return;
    }
    if (!zip) {
      showSnackbar({ message: "Postal code is required", severity: "warning" });
      return;
    }
    if (!variantId) {
      showSnackbar({ message: "Please select a product", severity: "warning" });
      return;
    }
    if (!shippingMethodId) {
      showSnackbar({ message: "Please select a shipping method", severity: "warning" });
      return;
    }

    setIsSubmitting(true);
    try {
      await createSaleorOrder();
    } finally {
      setIsSubmitting(false);
    }
  };

  const createSaleorOrder = async () => {
    try {
      const payload = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phone, // already normalized
        variantId,
        userAddress: {
          street_address: streetAddress.trim(),
          street_address2: streetAddress2.trim(),
          locality: locality.trim(),
          region: region.trim(),
          postal_code: postalCode.trim()
        },
        shippingMethodId,
        amountPaid: invoiceAmount ? Number(invoiceAmount) : 0
      };

      const response = await ApiService.createSaleorOrder(payload);
      showSnackbar({ message: "Order created successfully!", severity: "success" });
      setFirstName("");
      setLastName("");
      setEmail("");
      setPhone("");
      setStreetAddress("");
      setStreetAddress2("");
      setLocality("");
      setRegion("");
      setPostalCode("");
      setVariantId("");
      setShippingMethodId("");
      setInvoiceAmount(0);
    } catch (error) {
      console.error("Error creating order:", error);
      showSnackbar({ message: "Failed to create order, please confirm if the User info and Address is correct", severity: "error" });
      MonitorService.captureException(error);
    }
  };

  return (
    <Container fluid>
      {/* Top-left Back & Cancel buttons */}
      <Row className="mb-3">
        <Col>
          <Button variant="outline-secondary" onClick={resetView} style={{ marginRight: "8px" }}>
            &larr; Back
          </Button>
          <Button
            variant="outline-danger"
            onClick={() => {
              // Clear all fields and/or navigate away
              setFirstName("");
              setLastName("");
              setEmail("");
              setPhone("");
              setStreetAddress("");
              setStreetAddress2("");
              setLocality("");
              setRegion("");
              setPostalCode("");
              setVariantId("");
              setShippingMethodId("");
              setInvoiceAmount("");
            }}
          >
            Clear
          </Button>
        </Col>
      </Row>

      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <div
            style={{
              textAlign: "center",
              marginBottom: "5px",
              fontSize: "28px",
              fontWeight: "600"
            }}
          >
            Create an Order on Saleor {kitName ? `for ${kitName}` : ""}
          </div>
          <div style={{ textAlign: "center", color: "#555", marginBottom: "20px" }}>
            This page is for creating orders for customers without a payment
          </div>
          <Card
            style={{
              background: "#fff",
              color: "#333",
              borderRadius: "8px",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
              padding: "30px",
              border: "1px solid #e0e0e0",
              borderTop: "5px solid #007F61"
            }}
          >
            <Card.Body style={{ padding: 0 }}>
              <Form onSubmit={handleSubmit}>
                <div style={{ marginBottom: "16px" }}>
                  <h5 style={{ color: "#333", fontWeight: "600", fontSize: "16px" }}>Personal Information</h5>
                </div>

                {/* First and Last Name */}
                <Row style={{ marginBottom: "16px" }}>
                  <Col md={6}>
                    <Form.Group controlId="firstName">
                      <Form.Label style={labelStyle}>First Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter first name"
                        value={firstName}
                        onChange={e => setFirstName(e.target.value)}
                        style={inputStyle}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="lastName">
                      <Form.Label style={labelStyle}>Last Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter last name"
                        value={lastName}
                        onChange={e => setLastName(e.target.value)}
                        style={inputStyle}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Email and Phone */}
                <Row style={{ marginBottom: "16px" }}>
                  <Col md={6}>
                    <Form.Group controlId="email">
                      <Form.Label style={labelStyle}>Email</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="Enter email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        style={inputStyle}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="phone">
                      <Form.Label style={labelStyle}>Phone</Form.Label>
                      <Form.Control
                        type="tel"
                        placeholder="Enter phone (+XXXXXXXXXXX)"
                        value={phone}
                        onChange={e => setPhone(e.target.value)}
                        style={inputStyle}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div style={{ margin: "24px 0 16px" }}>
                  <h5 style={{ color: "#333", fontWeight: "600", fontSize: "16px" }}>Address Details</h5>
                </div>

                {/* Street Address (full width) */}
                <Form.Group controlId="streetAddress" style={{ marginBottom: "16px" }}>
                  <Form.Label style={labelStyle}>Street Address</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Street address"
                    value={streetAddress}
                    onChange={e => setStreetAddress(e.target.value)}
                    style={inputStyle}
                  />
                </Form.Group>

                {/* Address Line 2 (half width) */}
                <Row style={{ marginBottom: "16px" }}>
                  <Col md={6}>
                    <Form.Group controlId="streetAddress2">
                      <Form.Label style={labelStyle}>Address Line 2</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Address line 2"
                        value={streetAddress2}
                        onChange={e => setStreetAddress2(e.target.value)}
                        style={inputStyle}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId="postalCode">
                      <Form.Label style={labelStyle}>Postal Code</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Postal code"
                        value={postalCode}
                        onChange={e => setPostalCode(e.target.value)}
                        style={inputStyle}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* City and Region (two columns) */}
                <Row style={{ marginBottom: "16px" }}>
                  <Col>
                    <Form.Group controlId="locality" style={{ marginBottom: 0 }}>
                      <Form.Label style={labelStyle}>City</Form.Label>
                      <Form.Control type="text" placeholder="City" value={locality} onChange={e => setLocality(e.target.value)} style={inputStyle} />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group controlId="region" style={{ marginBottom: 0 }}>
                      <Form.Label style={labelStyle}>Region</Form.Label>
                      <Form.Select value={region} onChange={e => setRegion(e.target.value)} style={inputStyle}>
                        <option value="">Select State</option>
                        {statesList.map(item => (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Variant (full width) */}
                <Form.Group controlId="variantId" style={{ marginBottom: "16px" }}>
                  <Form.Label style={labelStyle}>Select a Product</Form.Label>
                  <Form.Select value={variantId} onChange={e => setVariantId(e.target.value)} style={inputStyle}>
                    <option value="">Select a product</option>
                    {PRODUCTS.map(product => (
                      <option key={product.variantId} value={product.variantId}>
                        {product.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                {/* Shipping Method (full width) */}
                <Form.Group controlId="shippingMethodId" style={{ marginBottom: "16px" }}>
                  <Form.Label style={labelStyle}>Shipping Method</Form.Label>
                  <Form.Select value={shippingMethodId} onChange={e => setShippingMethodId(e.target.value)} style={inputStyle}>
                    <option value="">Select shipping method</option>
                    {SHIPPING.map(method => (
                      <option key={method.variantId} value={method.variantId}>
                        {method.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                {/* Invoice Amount (number input) */}
                <Form.Group controlId="invoiceAmount" style={{ marginBottom: "16px" }}>
                  <Form.Label style={labelStyle}>Invoice Amount (USD)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Enter invoice amount"
                    value={invoiceAmount}
                    onChange={e => setInvoiceAmount(e.target.value)}
                    style={inputStyle}
                  />
                </Form.Group>

                <hr style={{ borderColor: "#e0e0e0", margin: "24px 0" }} />

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    background: "#007F61",
                    border: "none",
                    borderRadius: "8px",
                    padding: "14px",
                    fontWeight: "600",
                    width: "100%",
                    color: "#fff",
                    marginTop: "12px",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                    opacity: isSubmitting ? 0.6 : 1,
                    cursor: isSubmitting ? "not-allowed" : "pointer"
                  }}
                >
                  <IoSend style={{ marginRight: "8px" }} />
                  {isSubmitting ? "Submitting..." : "Submit Order"}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default CreateOrder;
